import React from 'react';
import { Box, Button, InfoBanner } from '@sparknz/spark-pattern-library';
import { LinkRegion, trackCustomLink } from '@sparknz/tracking';

import { WearableIneligibleLineReason } from '__generated__/WearableEligibilityAccountSelectorQuery.graphql';
import { useProductDetails } from 'journey/productDetails/provider';
import { ProductDetailsActionTypes } from 'journey/productDetails/reducer/productDetails.reducer';
import { InfoLayerStatus } from 'journey/productDetails/typings';
import { useSegment } from 'shared/hooks';
import {
  getWarningMessage,
  isIncompatiblePlanCheck,
} from '../../../../utils/warningMessage.utils';
import { useTrackIneligibleWarningEvent } from '../../../hooks';

interface IneligibleWarningProps {
  reason?: WearableIneligibleLineReason;
  hasLines: boolean;
}

const COMPONENT_ID = 'productDetails.checkEligibility.wearablePlanDetails';
const COMPONENT_NAME = 'Wearable Plan Details';
const SUB_CATEGORY = 'Wearable plan';

const IneligibleWarning = ({ reason, hasLines }: IneligibleWarningProps) => {
  const { isBusiness } = useSegment();
  const { dispatch } = useProductDetails();

  const segment = isBusiness ? 'business' : 'consumer';
  const showEligiblePlanContentKey = `online-shop/plan-cards/key-conditions/${segment}/info-modals/eligible-plans`;

  const isIncompatiblePlan = isIncompatiblePlanCheck(reason);
  const showSeeEligiblePlansCTA =
    (isIncompatiblePlan || reason === 'PREPAID_LINE') && hasLines;

  const warningMessage = getWarningMessage({ reason, hasLines });

  useTrackIneligibleWarningEvent({
    componentID: COMPONENT_ID,
    componentName: COMPONENT_NAME,
    subCategory: SUB_CATEGORY,
    message: warningMessage,
  });

  const handleClick = (contentKey: string) => {
    dispatch({
      type: ProductDetailsActionTypes.SetInfoLayer,
      infoLayer: {
        contentKey,
        status: InfoLayerStatus.ContentFragment,
        isNested: true,
      },
    });

    trackCustomLink('See eligible plans');
  };

  return (
    <Box mb="s2" pt="s2">
      <InfoBanner variant="WARN">
        {warningMessage}&nbsp;
        {showSeeEligiblePlansCTA ? (
          <LinkRegion regionId="productDetails.line.message">
            <Button
              variant="textbutton"
              onClick={() => handleClick(showEligiblePlanContentKey)}
            >
              See eligible plans
            </Button>
          </LinkRegion>
        ) : null}
      </InfoBanner>
    </Box>
  );
};

export default IneligibleWarning;
